import { InfoCircleFilled } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Challenge,
  GET_IDEA,
  Idea,
  RESPOND_IDEA_CHALLENGE_INVITE,
} from '@frontend/data-access';
import { Button } from 'antd';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

interface ChallengeInvite {
  id: string;
  challenge: Challenge;
}

export function IdeaDetailInviteChallenge({ idea }: { idea: Idea }) {
  const { t } = useTranslation('common');
  const router = useRouter();
  const [loading, setLoading] = useState<{
    reject: boolean;
    accept: boolean;
    id: string;
  }>({
    reject: false,
    accept: false,
    id: '',
  });

  const [respondIdeaChallengeInvite] = useMutation(
    RESPOND_IDEA_CHALLENGE_INVITE,
    {
      refetchQueries: [{ query: GET_IDEA, variables: { ideaId: idea.id } }],
      onCompleted(data) {
        if (
          data?.respondIdeaChallengeInvite?.status === 'ACCEPTED' &&
          data?.respondIdeaChallengeInvite?.newIdeaId
        ) {
          router.push({
            pathname: '/idea/create/[id]/scratch',
            query: { id: data?.respondIdeaChallengeInvite?.newIdeaId },
          });
        }
      },
    }
  );

  const accept = async (id: string) => {
    setLoading({ ...loading, accept: true, id });
    await respondIdeaChallengeInvite({ variables: { id, status: 'ACCEPTED' } });
    setLoading({ ...loading, accept: false });
  };

  const reject = async (id: string) => {
    setLoading({ ...loading, reject: true, id });
    await respondIdeaChallengeInvite({ variables: { id, status: 'REJECTED' } });
    setLoading({ ...loading, reject: false });
  };

  const pendingInvites = idea.challengeInvites.filter(
    (c) => c.status === 'PENDING'
  );

  if (!pendingInvites.length) {
    return null;
  }

  return (
    <div className="bg-white rounded-lg p-4 border border-gray-500">
      <div className="flex gap-2 text-blue-800">
        <span className="text-xl">
          <InfoCircleFilled />
        </span>
        <div>
          <div className="font-semibold">
            {pendingInvites.length === 1
              ? t('challenge_invite_singular', { count: pendingInvites.length })
              : t('challenge_invite_plural', { count: pendingInvites.length })}
          </div>
          <div className="text-gray-1000">
            {t('challenge_invite_apply_info')}
          </div>
        </div>
      </div>
      <div className="space-y-4 divide-y divide-gray-300">
        {pendingInvites.map((invite: ChallengeInvite) => (
          <div
            key={invite.id}
            className="flex items-center justify-between border-t border-gray-100 pt-3"
          >
            <div className="text-sm whitespace-normal">
              {t('apply_to_challenge_do_you')}"
              <Link
                target="_blank"
                href={`/intrapreneurship/challenge/${invite.challenge.id}`}
                className="text-blue-800 decoration-gray-400 underline"
              >
                {invite.challenge.title}
              </Link>
              "{t('apply_to_challenge_using_this')}
            </div>
            <div className="flex gap-2">
              <Button
                danger
                shape="round"
                onClick={() => reject(invite.id)}
                className="hover:text-red-500"
                type="text"
                disabled={loading.reject}
                loading={loading.reject && loading.id === invite.id}
              >
                {t('reject')}
              </Button>
              <Button
                type="primary"
                shape="round"
                onClick={() => accept(invite.id)}
                className="!bg-green-500 !hover:bg-green-300 !border-none"
                disabled={loading.accept}
                loading={loading.accept && loading.id === invite.id}
              >
                {t('accept')}
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default IdeaDetailInviteChallenge;
